<template>
  <!-- Sous 768 px en mode « menu » : un bouton qui ouvre le plan. Il garde ses
       LIBELLÉS — c'est ce que la barre d'ancres de la v1 perdait, ne laissant
       que des icônes nues. -->
  <nav v-if="isMenu" class="toc-menu" :aria-label="ariaLabel">
    <button
      type="button"
      class="toc-menu-trigger"
      :aria-expanded="String(menuOpen)"
      @click="menuOpen = !menuOpen"
    >
      <span class="toc-menu-trigger-text">
        <i class="fas fa-list-ul" aria-hidden="true"></i>
        {{ label }}<span v-if="activeLabel"> — {{ activeLabel }}</span>
      </span>
      <i class="fas fa-chevron-down toc-menu-chevron" :class="{ on: menuOpen }" aria-hidden="true"></i>
    </button>

    <div v-if="menuOpen" class="toc-menu-panel">
      <button
        v-for="(section, index) in sections"
        :key="section.id"
        type="button"
        class="toc-item"
        :class="{ on: section.id === activeId }"
        :aria-current="section.id === activeId ? 'true' : undefined"
        @click="pick(section.id)"
      >
        <span v-if="numbered" class="toc-num">{{ numberOf(index) }}</span>
        <span class="toc-item-label">{{ section.label }}</span>
      </button>
    </div>
  </nav>

  <nav v-else class="toc-rail" :aria-label="ariaLabel">
    <p class="toc-label">{{ label }}</p>
    <div class="toc-items">
      <button
        v-for="(section, index) in sections"
        :key="section.id"
        type="button"
        class="toc-item"
        :class="{ on: section.id === activeId }"
        :aria-current="section.id === activeId ? 'true' : undefined"
        @click="$emit('select', section.id)"
      >
        <span v-if="numbered" class="toc-num">{{ numberOf(index) }}</span>
        <span class="toc-item-label">{{ section.label }}</span>
      </button>
    </div>
    <slot name="footer"></slot>
  </nav>
</template>

<script setup>
/**
 * Le rail d'un sommaire qui suit la lecture — la moitié visible de
 * `useReadingToc`, qui en tient la mécanique.
 *
 * Il ne lit rien et ne défile rien : il reçoit les sections et l'identifiant
 * actif, il émet celui qu'on lui désigne. C'est ce partage qui permet à la
 * modale d'aide et à une page de documentation d'afficher le même plan sans
 * partager leur mise en page.
 *
 * Deux comportements sous 768 px, parce que les deux appelants n'ont pas la
 * même contrainte :
 *  - `hidden` (la modale) : le sommaire coûterait un écran entier avant la
 *    première phrase ;
 *  - `menu` (une page) : le plan reste atteignable, replié derrière un bouton.
 */
import { computed, nextTick, ref, watch } from 'vue'

const props = defineProps({
  /** `[{ id, label }]` — tel que `useReadingToc` le produit. */
  sections: {
    type: Array,
    default: () => []
  },
  activeId: {
    type: String,
    default: null
  },
  label: {
    type: String,
    default: 'Sommaire'
  },
  ariaLabel: {
    type: String,
    default: 'Sommaire'
  },
  /** Numérote les entrées (01, 02…) : utile quand le plan est long. */
  numbered: {
    type: Boolean,
    default: false
  },
  /** Rendu compact sous 768 px : `menu` (défaut) ou `hidden`. */
  mobile: {
    type: String,
    default: 'menu',
    validator: (value) => ['menu', 'hidden'].includes(value)
  },
  /** Vrai quand la fenêtre est sous 768 px (la vue le sait, pas le rail). */
  isMobile: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select'])

const menuOpen = ref(false)

const isMenu = computed(() => props.isMobile && props.mobile === 'menu')

const activeLabel = computed(
  () => props.sections.find((s) => s.id === props.activeId)?.label ?? null
)

const numberOf = (index) => String(index + 1).padStart(2, '0')

/**
 * Choisir une section referme le menu : sur un écran étroit, il recouvre
 * justement ce qu'on vient de demander à voir.
 *
 * La fermeture passe AVANT l'émission, et on attend que le DOM l'ait prise en
 * compte. Le panneau déplié occupe la hauteur de ses douze entrées — plus de
 * 500 px — et le destinataire de `select` mesure la position de la section
 * pour y défiler. Émettre d'abord, c'était mesurer sur une page dont le
 * panneau allait disparaître : on atterrissait une section trop bas, très
 * exactement de la hauteur du menu.
 */
const pick = async (id) => {
  menuOpen.value = false
  await nextTick()
  emit('select', id)
}

// Repasser en grand écran ne doit pas laisser un panneau ouvert derrière soi.
watch(isMenu, (menu) => {
  if (!menu) menuOpen.value = false
})
</script>

<style scoped>
/* ═══ Rail ═══ */
.toc-rail {
  display: flex;
  flex-direction: column;
}

.toc-label {
  margin: 0 var(--spacing-3) var(--spacing-2);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.toc-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ═══ Entrée — commune au rail et au menu ═══ */
.toc-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  width: 100%;
  text-align: left;
  padding: var(--spacing-2) var(--spacing-3);
  min-height: 40px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.toc-item:hover {
  background: var(--color-bg-hover);
  color: var(--color-text-primary);
}

.toc-item:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.toc-item.on {
  background: var(--color-nav-active-bg);
  border-color: var(--color-nav-active-border);
  color: var(--color-primary-dark);
  font-weight: var(--font-weight-semibold);
}

.toc-num {
  flex-shrink: 0;
  width: 20px;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted);
  text-align: right;
}

.toc-item.on .toc-num {
  color: var(--color-primary);
}

/* Un plan long tient rarement en une ligne : le libellé se coupe plutôt que
   d'élargir le rail. */
.toc-item-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ═══ Menu (sous 768 px) ═══ */
.toc-menu-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  width: 100%;
  /* Cible de navigation : 44 px, pas 36. */
  min-height: 44px;
  padding: var(--spacing-2) var(--spacing-4);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  cursor: pointer;
  text-align: left;
}

.toc-menu-trigger:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.toc-menu-trigger-text {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.toc-menu-chevron {
  flex-shrink: 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  transition: transform var(--transition-fast);
}

.toc-menu-chevron.on {
  transform: rotate(180deg);
}

.toc-menu-panel {
  margin-top: var(--spacing-2);
  padding: var(--spacing-2);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toc-menu-panel .toc-item {
  min-height: 44px;
}
</style>
